import React, { useEffect, useRef, useState } from 'react';
import { DEFAULT_AVATAR } from '../../../../shared/utils/matrix/matrixRoomUtils';
import CreatorCreditsNotifyModal from './component/CreatorCreditsNotifyModal';
import {
  formatHandle,
  getCallReconnectBannerLabel,
  getMatrixSdkCallStateLabel,
} from './utils/callFormatUtils';
import { CALL_MEDIA_TYPE } from './constant';

const VideoCallUI = ({
  isCreator,
  totalTipCollectedAmount,
  tipText,
  localDisplayName = 'You',
  opponentDisplayName,
  opponentAvatar,
  callDuration,
  hasRemoteStream,
  isRemoteCameraOff = false,
  hasLocalStream,
  localVideoRef,
  remoteVideoRef,
  micMuted,
  sdkCallState,
  iceConnectionState,
  isOnline,
  bandwidthMode,
  onToggleMic,
  showSubscriberCameraToggle = false,
  cameraOff = false,
  onToggleCamera,
  onEndCall,
  onOpenCredits,
  onOpenTip,
  tipDisabled = false,
  showCreditsNotify,
  dismissCreditsNotify,
  creatorLowBalanceMinutesRemaining = null,
  showCameraFlip = false,
  cameraFlipBusy = false,
  cameraFlipDisabled,
  onFlipCamera,
  isCameraFlipped = false,
  isRemoteCameraFlipped = false,
}) => {
  const variantClass = isCreator ? 'video-creator' : 'video-subscriber';
  const flipControlDisabled = cameraFlipDisabled ?? cameraFlipBusy;
  const stageRef = useRef(null);
  const localPreviewRef = useRef(null);
  const dragStateRef = useRef({ dragging: false, pointerId: null, offsetX: 0, offsetY: 0 });
  const [localPreviewPosition, setLocalPreviewPosition] = useState(null);

  const callStateLabel = getMatrixSdkCallStateLabel(sdkCallState);

  const reconnectLabel = getCallReconnectBannerLabel({
    isOnline,
    iceConnectionState,
    sdkCallState,
    variant: CALL_MEDIA_TYPE.VIDEO,
  });

  const stateToneClass =
    reconnectLabel && iceConnectionState === 'failed'
      ? 'cm_call_state_failed'
      : sdkCallState === 'connected'
        ? 'cm_call_state_connected'
        : 'cm_call_state_pending';

  const clampPosition = (x, y) => {
    const stageEl = stageRef.current;
    const localEl = localPreviewRef.current;
    if (!stageEl || !localEl) return { x, y };
    const maxX = Math.max(0, stageEl.clientWidth - localEl.offsetWidth);
    const maxY = Math.max(0, stageEl.clientHeight - localEl.offsetHeight);
    return {
      x: Math.min(Math.max(x, 0), maxX),
      y: Math.min(Math.max(y, 0), maxY),
    };
  };

  useEffect(() => {
    const stageEl = stageRef.current;
    const localEl = localPreviewRef.current;
    if (!stageEl || !localEl) return undefined;

    const setDefaultPosition = () => {
      const rightGap = 14;
      const topGap = 20;
      const x = Math.max(0, stageEl.clientWidth - localEl.offsetWidth - rightGap);
      const y = topGap;
      setLocalPreviewPosition(clampPosition(x, y));
    };

    setDefaultPosition();

    const onResize = () => {
      setLocalPreviewPosition((prev) => {
        if (!prev) return prev;
        return clampPosition(prev.x, prev.y);
      });
    };

    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
    };
  }, []);

  useEffect(() => {
    const handlePointerMove = (event) => {
      const drag = dragStateRef.current;
      if (!drag.dragging) return;
      if (drag.pointerId !== null && event.pointerId !== drag.pointerId) return;
      event.preventDefault();

      const stageEl = stageRef.current;
      if (!stageEl) return;
      const stageRect = stageEl.getBoundingClientRect();
      const nextX = event.clientX - stageRect.left - drag.offsetX;
      const nextY = event.clientY - stageRect.top - drag.offsetY;
      setLocalPreviewPosition(clampPosition(nextX, nextY));
    };

    const handlePointerUp = (event) => {
      const drag = dragStateRef.current;
      if (!drag.dragging) return;
      if (drag.pointerId !== null && event.pointerId !== drag.pointerId) return;
      dragStateRef.current = { dragging: false, pointerId: null, offsetX: 0, offsetY: 0 };
    };

    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerUp);
    window.addEventListener('pointercancel', handlePointerUp);

    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
      window.removeEventListener('pointercancel', handlePointerUp);
    };
  }, []);

  const handlePreviewPointerDown = (event) => {
    const stageEl = stageRef.current;
    const localEl = localPreviewRef.current;
    if (!stageEl || !localEl) return;

    const stageRect = stageEl.getBoundingClientRect();
    const localRect = localEl.getBoundingClientRect();
    const fallbackX = localRect.left - stageRect.left;
    const fallbackY = localRect.top - stageRect.top;
    const currentPosition = localPreviewPosition || { x: fallbackX, y: fallbackY };

    dragStateRef.current = {
      dragging: true,
      pointerId: event.pointerId ?? null,
      offsetX: event.clientX - (stageRect.left + currentPosition.x),
      offsetY: event.clientY - (stageRect.top + currentPosition.y),
    };

    setLocalPreviewPosition(clampPosition(currentPosition.x, currentPosition.y));
  };

  const opponentHandle = formatHandle(opponentDisplayName);
  const showRemotePlaceholder = !hasRemoteStream || isRemoteCameraOff;

  return (
    <div className={`call-wrapper cm_call_ui cm_call_ui_${variantClass} ${showCreditsNotify ? 'cm_call_ui_voice_with_notify' : ''}`}>
      <CreatorCreditsNotifyModal
        open={showCreditsNotify}
        onDismiss={dismissCreditsNotify}
        minutesRemaining={creatorLowBalanceMinutesRemaining}
      />
      {isCreator && (
        <div className="cm_call_top_chip">
          Total collected: <span>{totalTipCollectedAmount}</span>{' '}
          <span className="cm_call_top_chip_emoji" role="img" aria-label="money bag">
            💰
          </span>
        </div>
      )}

      <div className="cm_call_stage" ref={stageRef}>
        <video
          ref={remoteVideoRef}
          autoPlay
          playsInline
          muted={false}
          controls={false}
          disablePictureInPicture
          controlsList="nodownload nofullscreen noremoteplayback noplaybackrate"
          className={`cm_call_remote_video${isRemoteCameraFlipped ? ' cm_call_remote_video_flipped' : ''}${showRemotePlaceholder ? ' cm_call_remote_video_hidden' : ''}`}
          aria-label="Remote video"
          aria-hidden={showRemotePlaceholder}
        />

        {showRemotePlaceholder && (
          <div className="cm_call_remote_fallback" role="status">
            <img
              src={opponentAvatar || DEFAULT_AVATAR}
              alt=""
              className="cm_call_remote_fallback_avatar"
            />
            <h3 className="cm_call_remote_fallback_name">{opponentHandle}</h3>
            {isRemoteCameraOff ? (
              <p className="cm_call_remote_fallback_caption">Camera off</p>
            ) : null}
          </div>
        )}

        <div className="cm_call_header_block">
          <h3 className="cm_call_header_handle">{opponentHandle}</h3>
          <div className="cm_call_header_timer">{callDuration}</div>
          {(callStateLabel || reconnectLabel) && (
            <div className={`cm_call_header_state ${stateToneClass} text-muted`}>
              {reconnectLabel || callStateLabel}
              {bandwidthMode === 'low' ? ' · Low bandwidth' : ''}
            </div>
          )}
        </div>

        <div
          ref={localPreviewRef}
          className="cm_call_local_preview"
          onPointerDown={handlePreviewPointerDown}
          style={
            localPreviewPosition
              ? {
                // left: `${localPreviewPosition.x}px`,
                top: `${localPreviewPosition.y}px`,
                right: '15px',
              }
              : undefined
          }
        >
          <video
            ref={localVideoRef}
            autoPlay
            playsInline
            muted
            controls={false}
            disablePictureInPicture
            controlsList="nodownload nofullscreen noremoteplayback noplaybackrate"
            className={`cm_call_local_video${isCameraFlipped ? ' cm_call_local_video_flipped' : ''}`}
            aria-label="Your local video"
          />
          {!hasLocalStream && !cameraOff && (
            <div className="cm_call_local_fallback">{localDisplayName || 'You'}</div>
          )}
          {showSubscriberCameraToggle && cameraOff && (
            <div className="cm_call_local_camera_off" aria-hidden="true">
              <i className="fa-solid fa-video-slash" />
            </div>
          )}
          <span
            className={`cm_call_pip_live_dot${showSubscriberCameraToggle && cameraOff ? ' cm_call_pip_live_dot_hidden' : ''}`}
            aria-hidden
            title="Live"
          />
        </div>
      </div>

      {isCreator && tipText ? (
        <div className="cm_call_tip_chip_video">
          {tipText}{' '}
          <span role="img" aria-label="party popper">
            🎉
          </span>
        </div>
      ) : null}

      <div
        className={`call_actions_btn cm_call_controls ${isCreator ? 'cm_call_controls_creator' : 'cm_call_controls_subscriber'
          }`}
      >
        <button
          type="button"
          onClick={onToggleMic}
          className={`cm_call_control_m ${micMuted ? 'cm_call_control_active' : ''}`}
          aria-label={micMuted ? 'Unmute mic' : 'Mute mic'}
        >
          <i className={`fa-solid fa-microphone${micMuted ? '-slash' : ''}`} />
          <small>Mute</small>
        </button>

        {showSubscriberCameraToggle && (
          <button
            type="button"
            onClick={() => onToggleCamera?.()}
            className={`cm_call_control_m cm_call_control_camera${cameraOff ? ' cm_call_control_active' : ''}`}
            aria-label={cameraOff ? 'Turn camera on' : 'Turn camera off'}
            aria-pressed={cameraOff}
          >
            <i className={`fa-solid fa-video${cameraOff ? '-slash' : ''}`} />
            <small>Camera</small>
          </button>
        )}

        {showCameraFlip && (
          <button
            type="button"
            onClick={() => !flipControlDisabled && onFlipCamera?.()}
            className={`cm_call_control_flip${isCameraFlipped ? ' cm_call_control_flip_active' : ''}${flipControlDisabled ? ' cm_call_control_disabled' : ''}`}
            aria-label={isCameraFlipped ? 'Flip camera on' : 'Flip camera off'}
            aria-busy={cameraFlipBusy}
            disabled={flipControlDisabled}
          >
            <i className="fa-solid fa-camera-rotate" />
            <small>Flip</small>
          </button>
        )}

        {!isCreator && (
          <>
            <button
              type="button"
              className="cm_call_control_chip"
              onClick={onOpenCredits}
              aria-label="Credits"
            >
              <i className="fa-solid fa-coins" />
              <small>Credits</small>
            </button>
            <button
              type="button"
              className={`cm_call_control_chip cm_call_control_tip${tipDisabled ? ' cm_call_control_disabled' : ''}`}
              onClick={() => !tipDisabled && onOpenTip?.()}
              aria-label="Tip"
              aria-disabled={tipDisabled}
              disabled={tipDisabled}
            >
              <i className="fa-solid fa-dollar-sign" />
              <small>Tip</small>
            </button>
          </>
        )}

        <button
          type="button"
          className="red_call"
          onClick={onEndCall}
          aria-label="End call"
        >
          <i className="fa-solid fa-phone" />
          <small>End</small>
        </button>
      </div>
    </div>
  );
};

export default VideoCallUI;
